import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from "chart.js";

import PungoSectionCard from "pungo-ui/PungoSectionCard";
import PungoOptionSelectionByButton from "pungo-ui/PungoOptionSelectionByButton";
import PungoDemoAccordion from "pungo-ui/PungoDemoAccordion";
import PungoDemoResultsCard from "pungo-ui/PungoDemoResultsCard";
import cost from "assets/AucaImages/Cost.svg";
import optimizedCost from "assets/AucaImages/OptimizedCost.svg";
import carbonEmissions from "assets/AucaImages/carbonEmissions.svg";
import reducedCarbonEmissions from "assets/AucaImages/reducedCarbonEmissions.svg";
import energy from "assets/AucaImages/Energy.svg";
import energyOptimized from "assets/AucaImages/EnergyOptimized.svg";
import { SummarySection, IndicatorSection, ActionSection, ContributionSection } from "./ResultSections";
import { selectProPlanResults } from "store/slices/plan";

import colors from "styles/export.module.scss";
import styles from "./index.module.scss";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ResultsPdf from "./ResultsPdf";
import { Divider } from "@mui/material";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const ResultsProSection: React.FC = () => {
  const { t } = useTranslation();
  const proPlanResults = useSelector(selectProPlanResults);

  const optimizationResults = proPlanResults?.optimization;
  const [optimizationResultsSelectedTabIndex, setOptimizationResultsSelectedTabIndex] = useState<number>(0);
  const option01List = [`${t("proPlan.tabs.energySourceSingular")}`, `${t("proPlan.tabs.emissions")}`, `${t("proPlan.tabs.costs")}`];

  const handleOptionSelect01 = (index: number, name: string) => {
    setOptimizationResultsSelectedTabIndex(index);
  };

  const getOptimizationResults = () => (
    <div id="refProOptimization">

      <PungoOptionSelectionByButton names={option01List} onSelect={handleOptionSelect01} minWidth={90} />
      <Divider className={styles.divider} />

      {optimizationResultsSelectedTabIndex === 0 && (
        <div className={styles.results}>
          <PungoDemoResultsCard
            description={t("DemoPage.OptimizationSection.currentEnergy")}
            result={`${optimizationResults.energyContentCurrent.toFixed(0)} kWh`}
            color={colors.red1}
            image={energy}
          />

          <PungoDemoResultsCard
            description={t("DemoPage.OptimizationSection.optimizedEnergy")}
            result={`${optimizationResults.energyContentOptimized.toFixed(0)} kWh`}
            color={colors.green1}
            image={energyOptimized}
            reducedEnergy={`${optimizationResults.energyContentReduced.toFixed(0)} kWh`}
          />
        </div>
      )}

      {optimizationResultsSelectedTabIndex === 1 && (
        <div className={styles.results}>
          <PungoDemoResultsCard
            description={t("DemoPage.OptimizationSection.currentCarbonEmissions")}
            result={`${optimizationResults.emissionWithOutOptimization.toFixed(0)} kg CO2-eq`}
            color={colors.red1}
            image={carbonEmissions}
          />

          <PungoDemoResultsCard
            description={t("DemoPage.OptimizationSection.optimizedCarbonEmissions")}
            result={`${optimizationResults.emissionOptimized.toFixed(0)} kg CO2-eq`}
            color={colors.green1}
            image={reducedCarbonEmissions}
            reducedEmissions={`${optimizationResults.emissionReduced.toFixed(0)} kg CO2-eq`}
          />
        </div>
      )}

      {optimizationResultsSelectedTabIndex === 2 && (
        <div className={styles.results}>
          <PungoDemoResultsCard description={t("DemoPage.OptimizationSection.currentCosts")} result={`${optimizationResults.costCurrent.toFixed(0)} USD`} color={colors.red1} image={cost} />
          <PungoDemoResultsCard
            description={t("DemoPage.OptimizationSection.optimizedCosts")}
            result={`${optimizationResults.costOptimized.toFixed(0)} USD`}
            color={colors.green1}
            image={optimizedCost}
            reducedCost={`${optimizationResults.costReduced.toFixed(0)} USD`}
          />
        </div>
      )}
    </div>
  );

  return (
    <PungoSectionCard title={t("DemoPage.ResultsSection.results")} id="refProResults" padding="no">
      <div className={styles.container}>
        <div className={styles.resultsContainer} id="refProEmissions">
          <SummarySection />
        </div>
        <div className={styles.resultsContainer}>
          <IndicatorSection />
        </div>
        <div className={styles.resultsContainer}>
          <ContributionSection />
        </div>
        <div className={styles.resultsContainer}>
          <ActionSection />
        </div>
        <div className={styles.resultsContainer}>
          <PungoDemoAccordion title={t("proPlan.labels.optimization")} content={getOptimizationResults()} />
        </div>
        <div id="refProDownload">
          <ResultsPdf />
        </div>
      </div>
    </PungoSectionCard>
  );
};

export default ResultsProSection;
